import React, { useState, useMemo } from 'react'

import { LinkOutlined, CheckCircleFilled, ClockCircleFilled, CloseCircleFilled, StopFilled, SyncOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { MultipleValue } from '@shared/table/Rules/component'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'

import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule } from '@shared/table/TableIndex'
import { Tag, Space, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface PickingMovementsListProps {
  role?: 'operator' | 'admin';
  service: TableService; // New
  pickingListId?: string | null;
}

interface PickingMovementRow {
  id: string;
  picking_list?: {
    id: string;
    status: string;
    display_name?: string;
  } | null;
  merchant?: { name?: string } | null;
  shelf?: { location?: string } | null;
  product?: {
    id: string;
    name?: string;
    sku?: string;
  } | null;
  quantity?: number;
  operator?: { name?: string } | null;
  created_at?: string;
  [key: string]: unknown;
}

const PickingMovementsList: React.FC<PickingMovementsListProps> = ({
  role = 'operator',
  service, // New
  pickingListId = null
}) => {
  const { t } = useTranslation()
  const [lastModified, setLastModified] = useState(0)

  const urlPrefix = role === 'operator' ? '/operator' : '/admin'

  const columns = useMemo(() => {
    const cols: unknown[] = [
      {
        nameKey: 'shared.picking_movements.list.columns.picking_list',
        sortable: false,
        width: '160px',
        wrap: true,
        persist: true,
        cell: ({ picking_list }: PickingMovementRow) => {
          if (!picking_list) {
            return <span className="text-muted">-</span>
          }

          const statusConfig: Record<string, { icon: React.ComponentType<{ style?: React.CSSProperties }>; color: string }> = {
            ready: { icon: ClockCircleFilled, color: '#1890ff' },
            processing: { icon: SyncOutlined, color: '#fa8c16' },
            done: { icon: CheckCircleFilled, color: '#52c41a' },
            canceled: { icon: CloseCircleFilled, color: '#ff4d4f' },
            terminated: { icon: StopFilled, color: '#8c8c8c' }
          }

          const config = statusConfig[picking_list.status] || statusConfig.ready
          const StatusIcon = config.icon

          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Space size={8}>
                <Tooltip title={t(`enums.picking_list.status.${picking_list.status}`)}>
                  <StatusIcon style={{ color: config.color, fontSize: '14px' }} />
                </Tooltip>
                <Link to={`/picking_lists/${picking_list.id}`}>{picking_list.display_name}</Link>
              </Space>
              <a
                href={`${urlPrefix}/picking_lists/${picking_list.id}`}
                target="_blank"
                onClick={(e) => e.stopPropagation()}
                style={{ flexShrink: 0, marginLeft: '8px' }} rel="noreferrer"
              >
                <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
              </a>
            </div>
          )
        },
      },
      {
        nameKey: 'shared.picking_movements.list.columns.merchant',
        sortable: false,
        width: '120px',
        wrap: true,
        selector: ({ merchant }: PickingMovementRow) => merchant?.name || '-',
      },
      {
        nameKey: 'shared.picking_movements.list.columns.shelf',
        sortable: false,
        width: '100px',
        wrap: true,
        cell: ({ shelf }: PickingMovementRow) => shelf?.location ? (
          <Tag color="blue">{shelf.location}</Tag>
        ) : (
          <span className="text-muted">-</span>
        ),
      },
      {
        nameKey: 'shared.picking_movements.list.columns.product',
        sortable: false,
        minWidth: '200px',
        grow: 1,
        wrap: true,
        cell: ({ product }: PickingMovementRow) => {
          if (!product) {
            return <span className="text-muted">-</span>
          }

          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Link to={`/products/${product.id}`} style={{ wordBreak: 'break-word' }}>
                <div>
                  <div>{product.name || '-'}</div>
                  <div style={{ fontSize: '12px', color: '#999' }}>{product.sku || '-'}</div>
                </div>
              </Link>
              <a
                href={`${urlPrefix}/products/${product.id}`}
                target="_blank"
                onClick={(e) => e.stopPropagation()}
                style={{ flexShrink: 0, marginLeft: '8px' }} rel="noreferrer"
              >
                <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
              </a>
            </div>
          )
        },
      },
      {
        nameKey: 'shared.picking_movements.list.columns.quantity',
        name: (
          <Space size={4}>
            {t('shared.picking_movements.list.columns.quantity')}
            <Tooltip title={t('shared.picking_movements.list.column_tooltips.quantity')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        width: '80px',
        wrap: true,
        selector: ({ quantity }: PickingMovementRow) => quantity || 0,
      },
      {
        nameKey: 'shared.picking_movements.list.columns.operator',
        sortable: false,
        width: '100px',
        wrap: true,
        selector: ({ operator }: PickingMovementRow) => operator?.name || '-',
      },
      {
        nameKey: 'common.created_at',
        sortable: true,
        sortFieldName: 'shelf_movements.created_at',
        width: '180px',
        wrap: true,
        selector: ({ created_at }: PickingMovementRow) => created_at,
      },
    ]

    return cols
  }, [role, urlPrefix, t])

  const supportRules = useMemo<SupportRule[]>(() => {
    const rules: SupportRule[] = [
      {
        label: t('common.created_at'),
        component: FullDayRangePicker,

        inputName: 'shelf_movements.created_at',
        defaultPinned: true,
        tooltip: {
          title: t('shared.picking_movements.list.filter_tooltips.created_at.title'),
          content: t('shared.picking_movements.list.filter_tooltips.created_at.content'),
        },
      },
      {
        label: t('shared.picking_movements.list.filters.product_sku'),
        component: MultipleValue,
        inputName: 'products.sku',
        createable: true,
        defaultPinned: true,
        tooltip: {
          title: t('shared.picking_movements.list.filter_tooltips.product_sku.title'),
          content: t('shared.picking_movements.list.filter_tooltips.product_sku.content'),
        },
      },
      {
        label: t('shared.picking_movements.list.filters.shelf_location'),
        component: MultipleValue,
        inputName: 'shelves.location',
        createable: true,
        tooltip: {
          title: t('shared.picking_movements.list.filter_tooltips.shelf_location.title'),
          content: t('shared.picking_movements.list.filter_tooltips.shelf_location.content'),
        },
      },
      {
        label: t('shared.picking_movements.list.filters.operator'),
        component: MultipleValue,
        inputName: 'operators.name',
        createable: true,
        tooltip: {
          title: t('shared.picking_movements.list.filter_tooltips.operator.title'),
          content: t('shared.picking_movements.list.filter_tooltips.operator.content'),
        },
      },
      {
        label: t('models.picking_list.status'),
        component: MultipleValue,
        inputName: 'picking_lists.status',
        createable: false,
        defaultOptions: [
          { label: t('enums.picking_list.status.ready'), value: 'ready' },
          { label: t('enums.picking_list.status.processing'), value: 'processing' },
          { label: t('enums.picking_list.status.done'), value: 'done' },
          { label: t('enums.picking_list.status.canceled'), value: 'canceled' },
          { label: t('enums.picking_list.status.terminated'), value: 'terminated' },
        ],
        tooltip: {
          title: t('shared.picking_movements.list.filter_tooltips.status.title'),
          content: t('shared.picking_movements.list.filter_tooltips.status.content'),
        },
      },
    ]

    // Only show picking_list_id filter if not already filtered
    if (!pickingListId) {
      rules.push({
        label: t('models.picking_list.picking_list_id'),
        component: MultipleValue,
        inputName: 'picking_lists.id',
        createable: true,
        tooltip: {
          title: t('shared.picking_movements.list.filter_tooltips.picking_list_id.title'),
          content: t('shared.picking_movements.list.filter_tooltips.picking_list_id.content'),
        },
      })
    }

    return rules
  }, [t, pickingListId])

  // Build default query
  const defaultQuery = useMemo<Record<string, unknown>>(() => {
    const query: Record<string, unknown> = {
      sorting_field: 'shelf_movements.created_at',
      sorting_direction: 'desc',
    }

    if (pickingListId) {
      query['picking_lists.id'] = [pickingListId]
    }

    return query
  }, [pickingListId])

  return (
    <TableIndex
      columns={columns}
      searchable={{ match: 'contains', searchFields: [t('shared.picking_movements.list.filters.product_sku'), t('models.product.name')] }}
      columnsEditable={true}
      tableId={`${role === 'operator' ? 'Operator' : 'Admin'}PickingMovementsTable`}
      defaultQuery={defaultQuery}
      supportRules={supportRules}
      lastModified={lastModified}
      setLastModified={setLastModified}
      service={service}
      filterHelpExample={t('shared.picking_movements.list.filter_help_example') as string}
      filterDocsUrl={`/docs/${role}/picking_lists/history#features`}
    />
  )
}

export default PickingMovementsList
